import { Dispatch, SetStateAction, useMemo } from 'react';

import { Box, Card } from '@aicon-dev/caas-core';
import { WidgetGantt } from '@aicon-dev/caas-gantt';
import { Skeleton } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { HiOutlineFlag } from 'react-icons/hi';
import styled from 'styled-components';

import { useGanttLocale } from '~/modules/schedule/libs/hooks/useGanttLocale';

import {
  ICloseTodayRfis,
  IDashboardInitialProps,
  ITodayDrawings,
  ITodayPostings,
  ITodayWorkDateReports,
} from '../../libs/types';

import { weekContainer } from './styles.css';
import { WeekItem } from './WeekItem';
import WeeklyPlanListItem from './WeeklyPlanListItem';

interface Props extends Pick<IDashboardInitialProps, 'schedule'> {
  isLoading: boolean;
  selectedDate: string;
  setSelectedDate: Dispatch<SetStateAction<string>>;
  closeTodayRfis: ICloseTodayRfis[];
  todayDrawings: ITodayDrawings[];
  todayPostings: ITodayPostings[];
  todayWorkDateReports: ITodayWorkDateReports[];
}

const WeeklyPlan = ({
  isLoading,
  selectedDate,
  setSelectedDate,
  schedule,
  closeTodayRfis,
  todayDrawings,
  todayPostings,
  todayWorkDateReports,
}: Props) => {
  const { t, i18n } = useTranslation('project_dashboard');
  const { locale } = useGanttLocale();

  const startDate = useMemo(() => dayjs().subtract(2, 'day'), []);

  const allowedPeriod = useMemo(() => {
    return {
      start: dayjs().subtract(2, 'day'),
      end: dayjs().add(4, 'day'),
    };
  }, []);

  const checkPeriod = (startDate?: string | null, endDate?: string | null) => {
    if (!startDate) return false;
    if (!endDate) {
      if (dayjs(startDate).isBefore(allowedPeriod.start, 'day')) return false;
    } else {
      if (dayjs(endDate).isBefore(allowedPeriod.start, 'day')) return false;
    }
    if (dayjs(startDate).isAfter(allowedPeriod.end, 'day')) return false;
    return true;
  };

  /**
   * 허용된 기간 범위 내에 포함된 공정 데이터
   */
  const filteredProcesses = useMemo(() => {
    return (
      schedule?.data
        .filter((item) => {
          const matchedChildren = schedule.data.filter((child) => child.parent === item.id);

          /**
           * 공정 그룹 하위 자식들의 기간이 이번주에 속하는지 확인
           */
          if (item.isTopNode) {
            return !!matchedChildren.find((process) => {
              /**
               * 대상의 기간이 valid할 경우 true
               */
              if (checkPeriod(process.start_date, process.end_date)) return true;
              /**
               * 대상이 소유한 자식이 valid할 경우 true
               */
              return !!schedule?.data.find(
                (task) => task.parent === process.id && checkPeriod(task.start_date, task.end_date),
              );
            });
          }

          /**
           * 공정 하위 자식들의 기간이 이번주에 속하는지 확인
           */
          if (item.isSecondDepthNode) {
            if (checkPeriod(item.start_date, item.end_date)) return true;
            return !!matchedChildren.find((child) => checkPeriod(child.start_date, child.end_date));
          }

          /**
           * 여기서부터는 세부 공정입니다.
           */
          return checkPeriod(item.start_date, item.end_date);
        })
        .map((item) => {
          return {
            ...item,
            /**
             * 이번주 내에 포함되는 작업보고만 표시
             */
            work_days: item.work_days?.filter((e) => checkPeriod(e.start, e.end)),
          };
        }) || []
    );
  }, [schedule?.data]);

  /**
   * 상단에 표시할 공정 데이터
   */
  const weeklyProcesses = useMemo(() => {
    return filteredProcesses
      .filter((e) => {
        /**
         * 공정 그룹은 표시하지 않는다.
         */
        if (e.isTopNode) return false;
        /**
         * 대상이 자식을 가졌을 경우 표시하지 않는다.
         */
        const hasChild = !!schedule?.data?.find((child) => child.parent === e.id);

        if (dayjs(e.end_date).isBefore(dayjs(), 'day')) return false;
        if (hasChild) return false;
        return true;
      })
      .map((e) => {
        return {
          id: e.id,
          parent: schedule?.data?.find((parentNode) => {
            if (parentNode.id !== e.parent) return false;
            /**
             * 부모가 공정 그룹일 경우 표시하지 않는다.
             */
            if (parentNode.isTopNode) return false;
            return true;
          }),
          name: e.text,
        };
      });
  }, [schedule?.data, filteredProcesses]);

  const thisWeek = useMemo(
    () =>
      [...Array(7)].map((_, index) => {
        const date = dayjs(startDate).add(index, 'day').format('YYYY-MM-DD');

        return {
          date,
          isHoliday: index,
        };
      }),
    [startDate],
  );

  return (
    <Card
      title="이번주 공사 일정"
      titleStyle={{ fontSize: '18px', fontWeight: 600, padding: '1rem' }}
      bodyStyle={{ padding: 0 }}
    >
      <div className={weekContainer}>
        {thisWeek.map((week) => {
          return (
            <WeekItem
              key={week.date}
              week={week}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          );
        })}
      </div>

      {isLoading ? (
        <Box style={{ background: 'white' }}>
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
          <Skeleton active />
        </Box>
      ) : (
        <>
          {weeklyProcesses.length ? (
            <Box
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                background: 'white',
                padding: '1rem 1rem 0 1rem',
              }}
            >
              {weeklyProcesses?.map((process) => (
                <ProcessFlag key={process.id} color="#FFD166">
                  <HiOutlineFlag
                    style={{
                      flex: '0 0 auto',
                    }}
                  />
                  <span>
                    {!!process.parent?.text && <span>{`${process.parent?.text} > `}</span>}

                    <span>{process.name}</span>
                  </span>
                </ProcessFlag>
              ))}
            </Box>
          ) : null}

          <Box style={{ padding: '1rem', background: 'white' }}>
            <WidgetGantt
              rangeStart={dayjs(selectedDate || undefined)
                .subtract(2, 'day')
                .startOf('day')
                .format()}
              rangeEnd={dayjs(selectedDate || undefined)
                .add(4, 'day')
                .endOf('day')
                .format()}
              locale={locale}
              language={i18n.resolvedLanguage}
              dataSource={schedule?.data || []}
              viewOptions={{
                timelapse: false,
                drawing: false,
                rfi: false,
                specialNote: false,
                milestone: false,
              }}
              scaleUnit="day"
              height="246px"
              hideSideArea
            />
          </Box>

          {!closeTodayRfis?.length &&
          !todayWorkDateReports?.length &&
          !todayPostings?.length &&
          !todayDrawings?.length ? (
            <Box
              style={{
                height: '100px',
                padding: '1rem 0',
                background: 'white',
                textAlign: 'center',
              }}
            >
              {t('project_dashboard:m_no_registered_post')}
            </Box>
          ) : (
            <WeeklyPlanListItem
              closeTodayRfis={closeTodayRfis}
              todayDrawings={todayDrawings}
              todayPostings={todayPostings}
              todayWorkDateReports={todayWorkDateReports}
            />
          )}
        </>
      )}
    </Card>
  );
};

export default WeeklyPlan;

// const WeekWrapper = styled.div`
//   position: relative;
//   display: flex;
//   height: 52px;
//   padding: 0 0.5rem;
//   background: #fff;
//   border-bottom: 3px solid #eee;
// `;

const ProcessFlag = styled.div`
  display: flex;
  align-items: center;
  width: fit-content;
  min-height: 34px;
  padding: 5px 12px;
  margin-right: 8px;

  color: #262626;
  font-size: var(--font-size);
  font-weight: 400;
  line-height: 1.2;

  border: 1px solid #ffd166;
  border-radius: 4px;
  background-color: #fff;

  &:not(:last-child) {
    margin-bottom: 0.5rem;
  }

  & > svg {
    color: #ffd166;
    font-size: 21px;
    margin-right: 10px;
  }
`;
